import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useMemo, useState } from "react";
import { Controller, useFieldArray, useForm } from "react-hook-form";

import {
  FullScreenModal,
  FullScreenModalBody,
  FullScreenModalFooter,
  FullScreenModalHeader,
  Notification,
  TextTooltip,
} from "@transfr-inc/dashboard-components";
import { Button, Input } from "@transfr-inc/dashboard-components/forms";
import { Color } from "@transfr-inc/dashboard-components/utils";

import { useApiRequest } from "@lib/hooks";
import { useContainer } from "@src/contexts/container-context";
import { useIntegrationPreconfigEnabled } from "@queries";

import PreconfigView from "@views/integrations/list/preconfig/preconfig-view";

import "./add-integration.modal.scss";

export default ({
  open,
  provider,
  onCancel,
  onAddIntegration,
  addIntegrationFailed,
  connectionId,
  preconfigState, // true, false, or "active"
  clientCode,
  onPreconfigSync,
}) => {
  const { inputs = [], id, name } = provider;
  const { integrationService } = useContainer();

  const integrationPreconfigEnabled = useIntegrationPreconfigEnabled();

  const steps = {
    loading: "loading",
    input: "input",
    thirdPartyPending: "thirdPartyPending",
    preconfigLoading: "preconfigLoading",
    preconfig: "preconfig",
  };

  const [step, setStep] = useState(steps.input);
  const [preconfigTree, setPreconfigTree] = useState(null);

  const { response: icon = {} } = useApiRequest(
    () => integrationService.getProviderIconSquare(id),
    Boolean(id)
  );

  const {
    response: isPreconfigEnabled = false,
    loading: loading,
    error: preconfigEnabledError,
  } = useApiRequest(() => {
    if (!integrationPreconfigEnabled) return Promise.resolve(false);
    return integrationService.getPreconfigEnabledForProvider(name);
  }, open);

  useEffect(() => {
    if (preconfigState === "active" && step === steps.thirdPartyPending) {
      setStep(steps.preconfigLoading);
    }

    if (connectionId && clientCode && step === steps.preconfigLoading) {
      integrationService
        .triggerPreconfigSync(connectionId, clientCode)
        .then(() => {
          setTimeout(() => {
            loadPreconfigTree();
          }, 5000);
        });
    }
  }, [preconfigState, step, connectionId, clientCode]);

  const addDisplayTags = (items) => {
    return items.map((item) => ({
      ...item,
      displayTag:
        item.sourceId &&
        (item.inputKey === "canvas_org" ||
          item.inputKey === "canvas_subaccount")
          ? `ID: ${item.sourceId}`
          : null,
      children: addDisplayTags(item.children || []),
    }));
  };

  const loadPreconfigTree = async () => {
    try {
      const preconfigData = await integrationService.getPreconfigTree(
        connectionId,
        clientCode
      );

      if (!preconfigData || !preconfigData.items.length) {
        console.info("No preconfig data found, trying again soon.");
        setTimeout(loadPreconfigTree, 5000);
      } else {
        const updatedData = {
          ...preconfigData,
          items: addDisplayTags(preconfigData.items),
        };
        setPreconfigTree(updatedData);
        setStep(steps.preconfig);
      }
    } catch (error) {
      console.error("Failed to load preconfig tree:", error);
    }
  };

  const methods = useForm({
    criteriaMode: "all",
    mode: "onChange",
    defaultValues: {
      formInputs: inputs,
    },
    shouldUnregister: true,
  });

  const { control, handleSubmit, formState } = methods;
  const { isValid } = formState;

  const { fields } = useFieldArray({
    control,
    name: "formInputs",
  });

  const integrationInputList = useMemo(() => {
    if (fields.length > 0) {
      return fields
        .sort((a, b) => a.order - b.order || a.key.localeCompare(b.key))
        .map((field, index) => {
          switch (field.type) {
            case "string":
              return (
                <Controller
                  control={control}
                  name={`formInputs[${index}].${field.key}`}
                  key={field.id}
                  rules={{
                    required: {
                      value: true,
                      message: "Required fields are missing.",
                    },
                  }}
                  render={({ field: { onChange, onBlur, value } }) => {
                    return (
                      <div className="integrations-input-row">
                        <Input
                          className="integration-input"
                          label={removeUnderline(field.key)}
                          onChange={onChange}
                          onBlur={onBlur}
                          value={value}
                          name={field.key}
                        />
                        {field.helpText && (
                          <TextTooltip text={field.helpText}>
                            <FontAwesomeIcon icon="fa-solid fa-circle-info" />
                          </TextTooltip>
                        )}
                      </div>
                    );
                  }}
                />
              );

            default:
              return;
          }
        });
    }
  }, [fields]);

  const handleSync = () => {
    if (step === steps.preconfig) {
      onPreconfigSync(preconfigTree);
      return;
    }
    handleSubmit((data) => {
      const mapped = data.formInputs.map((input) => {
        const key = Object.keys(input)[0];
        const inputValue = input[key];
        const correspondingInput = fields.find((i) => i.key === key);
        return {
          key: key,
          value: inputValue,
          encrypted: correspondingInput.encrypted,
        };
      });
      onAddIntegration(mapped, id, isPreconfigEnabled);
      setStep(steps.thirdPartyPending);
    })();
  };

  const confirmButton = useMemo(() => {
    const integrateButton = (
      <Button primary onClick={handleSync} disabled={!isValid}>
        Integrate
      </Button>
    );
    const continueButton = (
      <Button
        primary
        onClick={handleSync}
        disabled={
          !isValid ||
          loading ||
          step === steps.preconfigLoading ||
          step === steps.thirdPartyPending
        }
      >
        {loading
          ? "Loading..."
          : isPreconfigEnabled &&
            !preconfigEnabledError &&
            step !== steps.preconfig
          ? "Continue"
          : "Integrate"}
      </Button>
    );
    return isPreconfigEnabled && !preconfigEnabledError
      ? continueButton
      : integrateButton;
  }, [
    isValid,
    isPreconfigEnabled,
    loading,
    preconfigEnabledError,
    step,
    preconfigTree,
  ]);

  return (
    <FullScreenModal
      open={open}
      className="sync-integration-modal"
      onClose={onCancel}
      drawerBgColor={Color.white}
    >
      <FullScreenModalHeader
        icon={["fa-regular", "swap"]}
        title="Add Integration"
      ></FullScreenModalHeader>
      <FullScreenModalBody>
        <div className="modal-container">
          <div className="image-container">
            <img className="provider-img" src={icon} />
          </div>
          {addIntegrationFailed && (
            <Notification type="error">
              Connection Failed. Please review inputs and try again.
            </Notification>
          )}
          {step === steps.input && integrationInputList}
          {step === steps.thirdPartyPending && (
            <div className="third-party-pending-message">
              <p>
                Please follow the instructions provided by the integration
                provider to complete the setup.
              </p>
            </div>
          )}
          {step === steps.preconfigLoading && (
            <div>
              <p>Loading preconfiguration data...</p>
            </div>
          )}
          {step === steps.preconfig && preconfigTree && (
            <div>
              <PreconfigView
                preconfigData={preconfigTree}
                setPreconfigTree={setPreconfigTree}
              />
            </div>
          )}
        </div>
      </FullScreenModalBody>
      <FullScreenModalFooter>
        <Button onClick={onCancel}>Cancel</Button>
        {confirmButton}
      </FullScreenModalFooter>
    </FullScreenModal>
  );
};

const removeUnderline = (str) => {
  return str
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};
